import { ACTION_TONES, type ActionTone } from '@claims/core';
import type { InOutKind } from './InOutBadge.js';

/** THE ROW KEY'S ONE DICTIONARY, SWEET CLAIMS' OWN (ported 2026-10-18 from the medrec console's lib/row-key.ts, its design plan's Lane 1; Rod
 *  there: "each row I'll need a circle i to explain what the inside may mean"; DESIGN_SYSTEM 8). Pure, no React. The medrec console's words
 *  are its own records (D, M and T, the record codes, the vendor's steps); these are Sweet Claims': M and I (the Matter and the insurance
 *  record in Salesforce), the sides, the colours. A row's key lists only the marks on that row (`KeyEntry`, drawn by `list/RowKey.tsx`).
 *
 *  WHERE A KEY'S WORDS LIVE (the medrec console's rule): a mark more than one screen draws (M and I, the sides, the colours) is said HERE,
 *  once; a mark only one screen draws is said beside the function that builds that screen's keys (the Queue: `queue-key.ts`; Today:
 *  `today.tsx`), reading the shared words from here. */

/** HOW A KEY ENTRY'S MARK IS DRAWN: as it appears on the row (a word or a value in its tone, the one quiet tag, the link letters, the in and
 *  out badge with its direction word). */
export type KeyMark =
  | { kind: 'text'; text: string; tone?: 'red' | 'amber' | 'accent' | 'id' }
  | { kind: 'tag'; icon?: string; words: string }
  | { kind: 'links'; letters: readonly SfLetter[] }
  | { kind: 'io'; io: InOutKind; text?: string };
/** One line of a key: the mark, then the plain words for it. */
export interface KeyEntry { id: string; mark: KeyMark; words: string }

export type SfLetter = 'M' | 'I';

/** THE BUTTONS' COLOUR KEY, the default footnote (ported 2026-10-25 from the medrec console's action-tones lane, its PR #356: its old line's
 *  "blue is progress or your next step" said the opposite of blue for an outside party, and so did this console's). Each tone's colour word
 *  and what its press does, in core's order (`ACTION_TONES`: red, amber, blue, green, grey); RowKey draws each colour's word in its own tone
 *  (`ToneKey`). The medrec console's words but its outside party: here blue goes to the carrier (a letter, an AI call). A row's red time is
 *  said by its Since entry ("It turns red after 3 days"). */
export const TONE_KEY_WORDS = Object.freeze({
  money: { colour: 'red', does: 'pays' },
  client: { colour: 'amber', does: 'to the client' },
  vendor: { colour: 'blue', does: 'to the carrier' },
  records: { colour: 'green', does: 'settles records' },
  neutral: { colour: 'grey', does: 'looks, hands off or says no' },
} satisfies Record<ActionTone, { colour: string; does: string }>);
export const TONE_KEY_LEAD = 'Buttons:';

export const KEY_WORDS = Object.freeze({
  title: 'What this row\u{2019}s marks mean',
  escCloses: 'Esc closes',
  /** The button's name and its hover. */
  buttonName: (who: string): string => `Key for ${who} row`,
  buttonHover: 'What this row\u{2019}s marks mean',
  /** The key column's header, for a screen reader (the column has no words of its own). */
  column: 'Key',
  /** The footnote a key ends with, unless its screen gives its own (`RowKey`'s `footnote`): the buttons' colour key as one line ("Buttons: red
   *  pays, amber to the client, ..."), the words RowKey draws in their tones (each colour's word held to the word after it by a no-break
   *  space, as drawn). */
  colours: `${TONE_KEY_LEAD} ${ACTION_TONES.map((t) => `${TONE_KEY_WORDS[t].colour}\u{00A0}${TONE_KEY_WORDS[t].does}`).join(' \u{00B7} ')}`,
});

/** M AND I (the Matter, this insurance record), in that order everywhere: the letter, the record, its link's hover and its spoken name. */
export const SF_LETTERS: readonly SfLetter[] = Object.freeze(['M', 'I']);
export const SF_LINK_WORDS: Readonly<Record<SfLetter, { record: string; title: string; spoken: string }>> = Object.freeze({
  M: { record: 'Matter', title: 'Open the Matter in Salesforce (new tab)', spoken: 'Matter in Salesforce (opens in a new tab)' },
  I: { record: 'Insurance', title: 'Open this insurance record in Salesforce (new tab)', spoken: 'Insurance record in Salesforce (opens in a new tab)' },
});
/** THE LETTERS' COLUMN HEADER (ported 2026-10-30 from the medrec console's needs-you-words lane, its PR #482, Rod's "2a" there: letters
 *  alone had to be learned): what the letters do. The Queue draws it over M and I, on two lines in their width, and sorts by it; Today names
 *  its letters' column with it for a screen reader. */
export const SF_COLUMN_WORDS = 'Open in Salesforce';
/** The links' line in a key: both, or the Matter alone (a side the case has no record of). */
export const sfKeyWords = (letters: readonly SfLetter[]): string =>
  letters.includes('I') ? 'The Matter and this insurance record in Salesforce. Each opens in a new tab.' : 'The Matter in Salesforce. It opens in a new tab.';

/** The one URL rule for a Salesforce record (the same address the case's Salesforce buttons open). */
const LIGHTNING = 'https://sweetjames.lightning.force.com/';
/** The row's two links: the Matter, and its insurance record (none for a side the case has no record of: the board's `gap:` rows). */
export function sfHrefs(matterId: string, insuranceId: string | null | undefined): Record<SfLetter, string | null> {
  const real = !!insuranceId && !insuranceId.startsWith('gap:');
  return { M: `${LIGHTNING}${matterId}`, I: real ? `${LIGHTNING}${insuranceId}` : null };
}
export const lettersOf = (hrefs: Readonly<Record<SfLetter, string | null>>): SfLetter[] => SF_LETTERS.filter((l) => hrefs[l] !== null);

/** THE SIDES, in plain words (the Queue, All cases and Today draw the same tags). */
export const SIDE_KEY_WORDS: Readonly<Record<string, string>> = Object.freeze({
  PLT: 'PLT: our client\u{2019}s own policy.',
  DEF: 'DEF: the at-fault driver\u{2019}s policy.',
  'Other Party': 'OP: another driver or party in the crash.',
  WC: 'WC: worker\u{2019}s comp.',
  HI: 'HI: health insurance, handled by the health LOR process.',
  Other: 'Other: an insurance record with no side set.',
});
/** The tag a side shows (Other Party is "OP"). */
export const sideTag = (side: string): string => (side === 'Other Party' ? 'OP' : side);
